<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetEditorFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    worksheet: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onRentalSelected = (...args) => {
    const { option, value } = args[0];
    if (option && option.client_id && option.client_name) {
      props.worksheet.client = {
        id: option.client_id,
        name: option.client_name,
      };
    }
  };

  const labelAlign = ref('right');
  const statuses = ref(null);
  const ux = ref([]);
  const b_8564662037462133_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));
  const b_3573211234170528_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));
  const b_6639903447389385_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));
  const b_8017867030266407_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
      contractors_only: true,
    },
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    statuses: statuses.value,
    ux: ux.value,
    b_8564662037462133_dataSource: b_8564662037462133_dataSource.value,
    b_3573211234170528_dataSource: b_3573211234170528_dataSource.value,
    b_6639903447389385_dataSource: b_6639903447389385_dataSource.value,
    b_8017867030266407_dataSource: b_8017867030266407_dataSource.value,
  }));

  const $$load_statuses = async () => {
    return $fn.value.getEnumValuesFromSchema('WorksheetStatus', {
      sort: false,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'statuses' },
      { name: 'ux' },
      { name: 'b_8564662037462133_dataSource', nodeId: '8564662037462133' },
      { name: 'b_3573211234170528_dataSource', nodeId: '3573211234170528' },
      { name: 'b_6639903447389385_dataSource', nodeId: '6639903447389385' },
      { name: 'b_8017867030266407_dataSource', nodeId: '8017867030266407' },
    ],
    loaders: { statuses: $$load_statuses },
    ux: {},
    dataRefs: {
      labelAlign,
      statuses,
      ux,
      b_8564662037462133_dataSource,
      b_3573211234170528_dataSource,
      b_6639903447389385_dataSource,
      b_8017867030266407_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return props.worksheet === 'locked';
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );

  const act_3573211234170528_script_s22dqs6ex_value_callable = async (
    $event
  ) => {
    return onRentalSelected($event.data);
  };
  const act_3573211234170528_script_s22dqs6ex_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3573211234170528_script_s22dqs6ex_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_5qaxjss96 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1hdqmgm6h =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5qaxjss96,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1hdqmgm6h],
    event: 'reload',
  });
  const act_3573211234170528_script_s22dqs6ex = new actionTypes.ScriptAction({
    actionArgs: act_3573211234170528_script_s22dqs6ex_getActionArgs,
  });
  const inst_evh_3573211234170528_optionSelected_fd324dsu4 =
    new actionTypes.EventHandlerImpl({
      action: act_3573211234170528_script_s22dqs6ex,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_3573211234170528_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3573211234170528_optionSelected_fd324dsu4],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_1hdqmgm6h,
    inst_evh_7315092382398562_reload,
    inst_evh_3573211234170528_optionSelected_fd324dsu4,
    inst_evh_3573211234170528_optionSelected,
  };

  const evh_7315092382398562_reload_1hdqmgm6h = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1hdqmgm6h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_optionSelected_fd324dsu4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3573211234170528_optionSelected_fd324dsu4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="company"
      :label="pgettext('Worksheet', 'Own company')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpSelect2
        name="company"
        v-model="worksheet.company"
        :value="worksheet.company"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.company =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_8564662037462133_dataSource"
        prefetch
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="status"
      :label="pgettext('Worksheet', 'Status')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      required
    >
      <ItpFormSelect2
        name="status"
        v-model="worksheet.status"
        :value="worksheet.status"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.status =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        valueField="value"
        textField="text"
        :options="statuses"
        required
        optionTemplate='<UcWorksheetStatus :value="option.value"></UcWorksheetStatus>'
        use-option-template-for-selected-option
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="number"
      :label="pgettext('Worksheet', 'Worksheet Number')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      required
    >
      <ItpFormInput
        name="number"
        v-model="worksheet.number"
        :value="worksheet.number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="isReadonly"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="description"
      :label="pgettext('Worksheet', 'Description')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="8"
      required
    >
      <ItpFormInput
        autofocus
        name="description"
        v-model="worksheet.description"
        :value="worksheet.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="isReadonly"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="gl_account_number"
      :label="pgettext('Worksheet', 'GL Account Number')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
    >
      <ItpFormInput
        name="gl_account_number"
        v-model="worksheet.gl_account_number"
        :value="worksheet.gl_account_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.gl_account_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      name="worksheet_date"
      :label="pgettext('Worksheet', 'Worksheet Date')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="2"
      required
    >
      <ItpFormDatetime
        name="worksheet_date"
        v-model="worksheet.worksheet_date"
        :readonly="isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="period"
      :label="pgettext('Worksheet', 'Period')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      contentClass="row"
    >
      <ItpFormDatetime
        name="start_date"
        v-model="worksheet.start_date"
        :placeholder="pgettext('Worksheet/StartDate/placeholder', 'Start')"
        :readonly="isReadonly"
        class="col-6"
      >
      </ItpFormDatetime>
      <ItpFormDatetime
        name="end_date"
        v-model="worksheet.end_date"
        :placeholder="pgettext('Worksheet/EndDate/placeholder', 'End')"
        :readonly="isReadonly"
        class="col-6"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      name="rental"
      :label="pgettext('Worksheet', 'Rental')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpSelect2
        name="rental"
        v-model="worksheet.rental"
        :value="worksheet.rental"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.rental =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :dataSource="b_3573211234170528_dataSource"
        valueField="id"
        textField="name"
        bind-object
        :readonly="isReadonly"
        use-option-template-for-selected-option
        optionTemplate='<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>'
        @optionSelected="evh_3573211234170528_optionSelected($event, {})"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="client"
      :label="pgettext('Worksheet', 'Partner')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpSelect2
        name="client"
        v-model="worksheet.client"
        :value="worksheet.client"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.client =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_6639903447389385_dataSource"
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="contractor"
      :label="pgettext('Worksheet', 'Contractor')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpSelect2
        name="contractor"
        v-model="worksheet.contractor"
        :value="worksheet.contractor"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.contractor =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_8017867030266407_dataSource"
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="notes"
      :label="pgettext('Worksheet', 'Notes')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="8"
    >
      <ItpFormTextarea
        name="notes"
        v-model="worksheet.notes"
        :value="worksheet.notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (worksheet.notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :readonly="isReadonly"
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
